import { useEffect, useMemo, useState } from 'react';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { UserProfileResult } from '@mews-commander/data-access';
import { Banner, Stack, StackItem } from '@optimus-web/core';
import { useIsUserProvisioned } from '../../hooks/useIsUserProvisioned';
import { useUserProfilesGet } from '../../queries';
import { hasDifferentRolesAndPermissions } from '../../utils';
import { FieldNames } from '../FieldNames';
import { EditUserForm } from './EditUserForm';
import { getOverriddenFields } from './formProps';

export const UserEditContent = ({ userProfile, isLoading }: { userProfile: UserProfileResult; isLoading: boolean }) => {
    const isUserProvisioned = useIsUserProvisioned(userProfile);
    const { data: detailedUser } = useUserProfilesGet(userProfile.userId);
    const fieldsConfig = useMemo(() => getOverriddenFields(detailedUser?.overrideComparator), [detailedUser]);

    const [lockedFields, setLockedFields] = useState<Partial<{ [key in FieldNames]: boolean }>>({});

    useEffect(() => {
        setLockedFields({
            [FieldNames.AssignedLearningPath]: fieldsConfig.includes('equalLearningPaths'),
            [FieldNames.EmployeeIncentiveCode]: fieldsConfig.includes('equalEmployeeIncentiveCodes'),
            [FieldNames.RolesAndPermissions]: hasDifferentRolesAndPermissions(fieldsConfig),
        });
    }, [fieldsConfig]);

    return (
        <Stack spacing="300" vertical>
            {isUserProvisioned && (
                <Banner
                    primaryText={<LocalizedText textKey={TextKey.UserManagementCannotEditProvisionedUserBannerTitle} />}
                    secondaryText={
                        <LocalizedText textKey={TextKey.UserManagementCannotEditProvisionedUserBannerText} />
                    }
                    variant="info"
                />
            )}

            {fieldsConfig.length > 0 ? (
                <Banner
                    primaryText={<LocalizedText textKey={TextKey.UserManagementValuesAreDifferentTitle} />}
                    secondaryText={<LocalizedText textKey={TextKey.UserManagementValuesAreDifferentText} />}
                    variant="warning"
                />
            ) : null}

            <StackItem>
                {isLoading ? null : (
                    <EditUserForm
                        userId={userProfile.userId}
                        lockedFields={lockedFields}
                        setLockedFields={setLockedFields}
                        fieldsConfig={fieldsConfig}
                    />
                )}
            </StackItem>
        </Stack>
    );
};
